import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { SkillGrowthData } from '../../model/my-team-comparison'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

interface SkillGrowthChartProps {
  skills: SkillGrowthData[]
  isLoading: boolean
  isDualMode: boolean
  entity1Name: string
  entity2Name?: string
}

export const SkillGrowthChart = observer(({
  skills,
  isLoading,
  isDualMode,
  entity1Name,
  entity2Name,
}: SkillGrowthChartProps) => {
  const { translation } = useLocalizationStore()
  const maxGrowth = Math.max(
    ...skills.map(s => Math.max(Math.abs(s.entity1Growth), Math.abs(s.entity2Growth || 0))),
    10,
  )

  if (isLoading) {
    return (
      <AppBox sx={styles.container}>
        <Caption sx={styles.title}>{translation.pages.myTeam.skillGrowth.title()}</Caption>
        <AppBox sx={styles.chartContainer}>
          <AppSkeleton count={5} height={40} width="100%" direction="column" />
        </AppBox>
      </AppBox>
    )
  }

  if (!skills || skills.length === 0) {
    return (
      <AppBox sx={styles.container}>
        <Caption sx={styles.title}>{translation.pages.myTeam.skillGrowth.title()}</Caption>
        <AppBox sx={styles.emptyState}>
          <Caption sx={styles.emptyText}>{translation.pages.myTeam.skillGrowth.noDataAvailable()}</Caption>
        </AppBox>
      </AppBox>
    )
  }

  return (
    <AppBox sx={styles.container}>
      <AppBox sx={styles.header}>
        <Caption sx={styles.title}>{translation.pages.myTeam.skillGrowth.title()}</Caption>
        <AppBox sx={styles.legend}>
          <AppBox sx={styles.legendItem}>
            <AppBox sx={{ ...styles.legendColor, backgroundColor: 'primary.main' }} />
            <Caption sx={styles.legendText}>{entity1Name}</Caption>
          </AppBox>
          {isDualMode && entity2Name && (
            <AppBox sx={styles.legendItem}>
              <AppBox sx={{ ...styles.legendColor, backgroundColor: 'secondary.main' }} />
              <Caption sx={styles.legendText}>{entity2Name}</Caption>
            </AppBox>
          )}
        </AppBox>
      </AppBox>

      <AppBox sx={styles.chartContainer}>
        {skills.map(skill => (
          <AppBox key={skill.skillName} sx={styles.skillRow}>
            <AppBox sx={styles.skillInfo}>
              <Caption sx={styles.skillName}>{skill.skillName}</Caption>
              <Caption sx={styles.categoryName}>{skill.categoryName}</Caption>
            </AppBox>

            <AppBox sx={styles.barsContainer}>
              <AppBox sx={styles.barRow}>
                <AppBox sx={styles.negativeBar}>
                  {skill.entity1Growth < 0 && (
                    <AppBox sx={{
                      ...styles.bar,
                      ...styles.entity1Bar,
                      width: `${(Math.abs(skill.entity1Growth) / maxGrowth) * 100}%`,
                      marginLeft: 'auto',
                    }}
                    />
                  )}
                </AppBox>
                <AppBox sx={styles.centerLine} />
                <AppBox sx={styles.positiveBar}>
                  {skill.entity1Growth >= 0 && (
                    <AppBox sx={{
                      ...styles.bar,
                      ...styles.entity1Bar,
                      width: `${(Math.abs(skill.entity1Growth) / maxGrowth) * 100}%`,
                    }}
                    />
                  )}
                </AppBox>
                <Caption sx={styles.growthValue}>
                  {skill.entity1Growth > 0 ? '+' : ''}{skill.entity1Growth.toFixed(1)}
                </Caption>
              </AppBox>

              {isDualMode && skill.entity2Growth !== undefined && (
                <AppBox sx={styles.barRow}>
                  <AppBox sx={styles.negativeBar}>
                    {skill.entity2Growth < 0 && (
                      <AppBox sx={{
                        ...styles.bar,
                        ...styles.entity2Bar,
                        width: `${(Math.abs(skill.entity2Growth) / maxGrowth) * 100}%`,
                        marginLeft: 'auto',
                      }}
                      />
                    )}
                  </AppBox>
                  <AppBox sx={styles.centerLine} />
                  <AppBox sx={styles.positiveBar}>
                    {skill.entity2Growth >= 0 && (
                      <AppBox sx={{
                        ...styles.bar,
                        ...styles.entity2Bar,
                        width: `${(Math.abs(skill.entity2Growth) / maxGrowth) * 100}%`,
                      }}
                      />
                    )}
                  </AppBox>
                  <Caption sx={styles.growthValue}>
                    {skill.entity2Growth > 0 ? '+' : ''}{skill.entity2Growth.toFixed(1)}
                  </Caption>
                </AppBox>
              )}
            </AppBox>
          </AppBox>
        ))}
      </AppBox>
    </AppBox>
  )
})

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  title: {
    fontSize: { xs: 11, md: 12 },
    color: 'text.secondary',
    fontWeight: 500,
  },
  legend: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap' as const,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  legendColor: {
    width: '12px',
    height: '12px',
    borderRadius: '10px',
  },
  legendText: {
    fontSize: 9,
    color: 'text.secondary',
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    paddingBottom: '10px',
  },
  skillRow: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: { xs: 6, sm: 10 },
    alignItems: { xs: 'flex-start', sm: 'center' },
  },
  skillInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: { xs: '100%', sm: '120px' },
    maxWidth: { xs: '100%', sm: '120px' },
  },
  skillName: {
    fontSize: { xs: 10, md: 11 },
    color: 'text.primary',
    fontWeight: 500,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  categoryName: {
    fontSize: 8,
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  barsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: '16px',
  },
  negativeBar: {
    flex: 1,
    display: 'flex',
    height: '100%',
  },
  centerLine: {
    width: '1px',
    height: '100%',
    backgroundColor: 'divider',
    flexShrink: 0,
  },
  positiveBar: {
    flex: 1,
    display: 'flex',
    height: '100%',
  },
  bar: {
    height: '100%',
    borderRadius: '10px',
    minWidth: '2px',
    transition: 'width 0.3s ease',
  },
  entity1Bar: {
    backgroundColor: 'primary.main',
  },
  entity2Bar: {
    backgroundColor: 'secondary.main',
  },
  growthValue: {
    fontSize: 9,
    color: 'text.primary',
    fontWeight: 500,
    minWidth: '35px',
    textAlign: 'right',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px 20px',
    flex: 1,
    minHeight: '200px',
  },
  emptyText: {
    fontSize: 10,
    color: 'text.secondary',
  },
}
